import { Card, CardList } from '@blueprintjs/core';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import AppData from 'Data/AppData';
import GenericTableData from 'Data/GenericTableData';
import DataManager from 'Lib/DataManager';
import { NOTE_UPDATE_ACTIONS } from 'Pages/Requests/Enum';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import AddNewButton from '../Buttons/AddNewButton';
import ButtonFooter from '../Buttons/ButtonFooter';
import UndoButton from '../Buttons/UndoButton';
import NoContentBox from '../Misc/NoContentBox';
import NoteCard from './NoteCard';

let DM = new DataManager();

const NotesListPanel = ({
  dataIdentifier,
  notesTableName,
  notesIdList,
  editable = false,
  canAddNotes = false,
  newNoteFieldValues = {},
  noteType = '',
  onNotesUpdated = () => {},
  ...props
}) => {
  const NotesDataRef = useRef(
    GenericTableData.getTableDataBus(dataIdentifier, notesTableName),
  );
  let NotesData = NotesDataRef.current;
  const [noteList, setNoteList] = useState([]);
  const [hasBeenUpdated, setHasBeenUpdated] = useState(false);

  useEffect(() => {
    DM.add(
      NotesData.registerListener('BLANK_RECORD_LOADED', onBlankNoteLoaded),
      NotesData,
    );
    DM.add(
      NotesData.registerListener('RECORDS_LOADED', onNoteListLoaded),
      NotesData,
    );
    DM.add(
      NotesData.registerListener('RECORDS_LOAD_ERROR', onNoteListLoadError),
      NotesData,
    );
    return () => {};
  }, []);

  useEffect(() => {
    fetchNotes();
  }, [notesIdList]);

  const fetchNotes = () => {
    if (notesIdList?.length) {
      NotesData.getRecords(notesIdList, true);
    } else {
      setNoteList([]);
    }
  };

  const onNoteListLoaded = (payload) => {
    setHasBeenUpdated(false);
    let notes = payload
      .slice()
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    setNoteList(notes);
  };

  const onNoteListLoadError = () => {};

  const onBlankNoteLoaded = (newNote) => {
    setHasBeenUpdated(true);
    newNote = {
      ...newNote,
      user: { full_name: AppData.userFullName() },
      user_id: AppData.userID(),
    };

    setNoteList((prevNotes) => [newNote, ...prevNotes]);
  };

  const onDeleteItem = (noteToDelete) => {
    setNoteList((prevNotes) =>
      [...prevNotes].filter((item) => item !== noteToDelete),
    );
    noteToDelete.id &&
      onNotesUpdated({
        action: NOTE_UPDATE_ACTIONS.DELETED,
        note: { id: noteToDelete.id },
      });
  };

  const undoChanges = () => {
    setNoteList([]);
    fetchNotes();
    setHasBeenUpdated(false);
  };

  const onNoteSaved = (isNew, payload) => {
    onNotesUpdated({
      action: isNew ? NOTE_UPDATE_ACTIONS.CREATED : NOTE_UPDATE_ACTIONS.UPDATED,
      note: payload,
    });
  };

  const newButton = canAddNotes && (
    <AddNewButton clickHandler={NotesData.getBlank} buttonText='Add Note' />
  );

  const undoButton = editable && hasBeenUpdated && (
    <UndoButton clickHandler={undoChanges} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {newButton}
    </ButtonFooter>
  );

  let getNotePanelsList = (inEditState) =>
    noteList.map((note, index) => {
      return (
        (inEditState || note.id) && (
          <Card
            key={`${note.id || index}-${note.createdAt}`}
            interactive={inEditState && note.user_id === AppData.userID()}>
            <NoteCard
              noteObject={note}
              editable={note.user_id === AppData.userID()}
              noteType={noteType}
              onUpdateNote={(isUpdated) =>
                setHasBeenUpdated((prevValue) => prevValue || isUpdated)
              }
              onSaveNote={onNoteSaved}
              onDeleteNote={() => onDeleteItem(note)}
              newNoteFieldValues={newNoteFieldValues}
              dataIdentifier={dataIdentifier}
              notesTableName={notesTableName}
            />
          </Card>
        )
      );
    });

  let getShouldShowNoContent = (inEditState) => {
    return !noteList || inEditState
      ? noteList.length === 0
      : noteList.filter((note) => note?.id).length === 0;
  };

  let getNoteListContent = (inEditState) =>
    (getShouldShowNoContent(inEditState) && (
      <NoContentBox label='No Notes' />
    )) || (
      <CardList bordered compact>
        {getNotePanelsList(inEditState)}
      </CardList>
    );

  return (
    <EditableDetailPanel
      title='Notes'
      editable={editable || canAddNotes}
      renderChild={getNoteListContent}
      footer={footer}
      {...props}
    />
  );
};

NotesListPanel.propTypes = {
  notesTableName: PropTypes.string.isRequired,
  dataIdentifier: PropTypes.string.isRequired,
  notesIdList: PropTypes.array.isRequired,
  editable: PropTypes.bool,
  canAddNotes: PropTypes.bool,
  newNoteFieldValues: PropTypes.object,
  noteType: PropTypes.string,
  onNotesUpdated: PropTypes.func,
  disabled: PropTypes.bool,
  collapsible: PropTypes.bool,
};

export default NotesListPanel;
